import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  BadgeArtwork,
  CertificateRun,
  CertificateRunPreview,
  CertificateSendLogItem,
  CohortBadges,
  Page,
} from "@/types";

/**
 * React Query hooks for the admin Certificates feature (KC-455): the per-cohort
 * run, its dry-run preview (recipients + exclusions + readiness), the gated
 * send actions, and the per-recipient send log. Follows the sources.ts slice
 * template.
 */

export interface CertificateSendLogParams {
  page: number;
  page_size: number;
  q?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const certificateKeys = {
  all: ["admin", "certificates"] as const,
  run: (programmeId: string) => [...certificateKeys.all, "run", programmeId] as const,
  preview: (runId: string) => [...certificateKeys.all, "preview", runId] as const,
  sendLog: (runId: string, params: CertificateSendLogParams) =>
    [...certificateKeys.all, "send-log", runId, params] as const,
  badges: (programmeId: string) => [...certificateKeys.all, "badges", programmeId] as const,
  artwork: () => [...certificateKeys.all, "badge-artwork"] as const,
};

/** The Cohort's credential, and — for Dominion Free — who has their badge (KC-497). */
export function useCohortBadges(programmeId: string | null) {
  return useQuery<CohortBadges>({
    queryKey: certificateKeys.badges(programmeId ?? "none"),
    queryFn: () => api.getCohortBadges(programmeId as string),
    enabled: programmeId !== null,
  });
}

export function useBadgeArtwork() {
  return useQuery<BadgeArtwork>({
    queryKey: certificateKeys.artwork(),
    queryFn: () => api.getBadgeArtwork(),
  });
}

export function useUploadBadgeArtwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadBadgeArtwork(file),
    onSuccess: (artwork) => queryClient.setQueryData(certificateKeys.artwork(), artwork),
  });
}

export function useResetBadgeArtwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.resetBadgeArtwork(),
    onSuccess: (artwork) => queryClient.setQueryData(certificateKeys.artwork(), artwork),
  });
}

export function useCertificateRun(programmeId: string | null) {
  return useQuery<CertificateRun | null>({
    queryKey: certificateKeys.run(programmeId ?? "none"),
    queryFn: () => api.getCertificateRun(programmeId as string),
    enabled: programmeId !== null,
  });
}

export function useCertificateRunPreview(runId: string | undefined) {
  return useQuery<CertificateRunPreview>({
    queryKey: certificateKeys.preview(runId ?? "none"),
    queryFn: () => api.getCertificateRunPreview(runId as string),
    enabled: runId !== undefined,
  });
}

export function useCertificateSendLog(runId: string | undefined, params: CertificateSendLogParams) {
  return useQuery<Page<CertificateSendLogItem>>({
    queryKey: certificateKeys.sendLog(runId ?? "none", params),
    queryFn: () => api.getCertificateSendLog(runId as string, params),
    enabled: runId !== undefined,
  });
}

function useInvalidateCertificates() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: certificateKeys.all });
}

export function useCreateCertificateRun() {
  const invalidate = useInvalidateCertificates();
  return useMutation({
    mutationFn: (programmeId: string) => api.createCertificateRun(programmeId),
    onSuccess: invalidate,
  });
}

export function useCertificateTestSend() {
  const invalidate = useInvalidateCertificates();
  return useMutation({
    mutationFn: ({ runId, userProgrammeId }: { runId: string; userProgrammeId?: string }) =>
      api.certificateTestSend(runId, userProgrammeId),
    onSuccess: invalidate,
  });
}

export function useApproveCertificateRun() {
  const invalidate = useInvalidateCertificates();
  return useMutation({
    mutationFn: (runId: string) => api.approveCertificateRun(runId),
    onSuccess: invalidate,
  });
}

export function useAuthoriseCertificateRun() {
  const invalidate = useInvalidateCertificates();
  return useMutation({
    mutationFn: ({ runId, issueDate }: { runId: string; issueDate?: string }) =>
      api.authoriseCertificateRun(runId, issueDate),
    onSuccess: invalidate,
  });
}

export function useRetryCertificateEmail() {
  const invalidate = useInvalidateCertificates();
  return useMutation({
    mutationFn: (scheduledEmailId: string) => api.retryCertificateEmail(scheduledEmailId),
    onSuccess: invalidate,
  });
}
